import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { isIdentityVerified } from '../../util/diditVerification';

import { AvatarLarge } from '../../components';

import { animalLabels, shortPlaceLabel } from '../../components/ListingCard/ListingCard.helpers';

import css from './ListingPage.module.css';

const firstNameOf = name => (typeof name === 'string' ? name.trim().split(/\s+/)[0] : '');

/**
 * The head of the sitter's profile (04_Profile_and_Booking/SPEC.md "Details"), with the sitter's
 * own card in it: the photo, the name with "✓ Verified", "{City, State} · {n} yrs with
 * livestock". The bio is not repeated here: About {First} shows it.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name the sitter's name
 * @param {Object} props.author the listing's author (user entity)
 * @param {Object} props.publicData the listing's public data
 * @returns {JSX.Element}
 */
export const SectionProfileHeader = props => {
  const { name, author, publicData } = props;
  const place = shortPlaceLabel(publicData?.location?.address);
  const years = Number(publicData?.yearsExperience);
  const hasYears = Number.isFinite(years) && years > 0;
  const authorId = author?.id?.uuid;

  return (
    <header className={css.profileHeader}>
      <div className={css.profileIdentity}>
        {authorId ? (
          <AvatarLarge className={css.profileAvatar} user={author} disableProfileLink />
        ) : null}
        <div className={css.profileIdentityText}>
          <div className={css.profileNameRow}>
            <h1 className={css.profileName}>{name}</h1>
            {isIdentityVerified(author) ? (
              <span className={css.profileVerified}>
                <span aria-hidden="true">✓</span> <FormattedMessage id="ListingCard.verified" />
              </span>
            ) : null}
          </div>
          {place || hasYears ? (
            <p className={css.profileMeta}>
              {place}
              {place && hasYears ? ' · ' : null}
              {hasYears ? (
                <FormattedMessage id="ListingPage.profile.years" values={{ years }} />
              ) : null}
            </p>
          ) : null}
        </div>
      </div>
    </header>
  );
};

/**
 * Animals, About {First} and What to expect (04_Profile_and_Booking/SPEC.md "Details").
 * Animals is left out when the listing has none; About shows the listing's description, or the
 * sitter's bio when there is none.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name the sitter's name
 * @param {Object} props.author the listing's author (user entity)
 * @param {Object} props.publicData the listing's public data
 * @param {string?} props.description the listing's description
 * @param {Array<Object>?} props.listingFields the marketplace's listing field configs
 * @returns {JSX.Element}
 */
export const SectionProfileDetails = props => {
  const { name, author, publicData, description, listingFields } = props;
  const firstName = firstNameOf(name);
  const animals = animalLabels(publicData, listingFields);
  const pd = publicData || {};
  // The listing's description, or the sitter's bio when the listing has none
  const about = description || author?.attributes?.profile?.bio;

  // Positive values are olive, the others muted
  const rows = [
    {
      key: 'videoConsult',
      positive: pd.videoConsult !== false,
      value:
        pd.videoConsult === false
          ? 'ListingPage.profile.onRequest'
          : 'ListingPage.profile.available',
    },
    ...(typeof pd.staysOvernight === 'boolean'
      ? [
          {
            key: 'staysOvernight',
            positive: pd.staysOvernight,
            value: pd.staysOvernight ? 'ListingPage.profile.yes' : 'ListingPage.profile.dayVisits',
          },
        ]
      : []),
    {
      key: 'identity',
      positive: isIdentityVerified(author),
      value: isIdentityVerified(author) ? 'ListingCard.verified' : 'ListingPage.profile.pending',
    },
  ];

  return (
    <>
      {animals.length > 0 ? (
        <section className={css.profileSection}>
          <h2 className={css.profileSectionTitle}>
            <FormattedMessage id="ListingPage.profile.animalsTitle" />
          </h2>
          <ul className={css.animalPills}>
            {animals.map(animal => (
              <li key={animal} className={css.animalPill}>
                {animal}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {about ? (
        <section className={css.profileSection}>
          <h2 className={css.profileSectionTitle}>
            <FormattedMessage id="ListingPage.profile.aboutTitle" values={{ name: firstName }} />
          </h2>
          <p className={css.profileAbout}>{about}</p>
        </section>
      ) : null}

      <section className={css.profileSection}>
        <h2 className={css.profileSectionTitle}>
          <FormattedMessage id="ListingPage.profile.expectTitle" />
        </h2>
        <dl className={css.expectList}>
          {rows.map(row => (
            <div key={row.key} className={css.expectRow}>
              <dt className={css.expectLabel}>
                <FormattedMessage id={`ListingPage.profile.${row.key}`} />
              </dt>
              <dd className={row.positive ? css.expectValuePositive : css.expectValue}>
                <FormattedMessage id={row.value} />
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  );
};
